/**
 * UTM capture — acquisition attribution for leads.
 *
 * The parameters are read from the current URL and, once seen, persisted in
 * sessionStorage so that a multi-step funnel (e.g. /en/start) still attributes
 * the lead even though the query string is lost on internal navigation.
 */

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const KEYS: (keyof UtmParams)[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const STORAGE_KEY = 'bpo-contact-utm-v1';

const clean = (value: string | null): string | undefined => {
  const trimmed = (value || '').trim().slice(0, 200);
  return trimmed.length ? trimmed : undefined;
};

/** Reads UTM params from the URL, falling back to the ones stored this session. */
export function captureUtmParams(): UtmParams {
  if (typeof window === 'undefined') return {};

  const search = new URLSearchParams(window.location.search);
  const fromUrl: UtmParams = {};
  for (const key of KEYS) {
    const value = clean(search.get(key));
    if (value) fromUrl[key] = value;
  }

  if (Object.keys(fromUrl).length > 0) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    } catch {
      /* storage unavailable — attribution stays request-scoped */
    }
    return fromUrl;
  }

  // GEO-2110: no UTM in the URL -> attribute a visit coming from an AI assistant
  // (host only, never the full referrer URL) and keep it for the session.
  const ai = aiReferrerSource(typeof document !== 'undefined' ? document.referrer : '');
  if (ai) {
    let hasStored = false;
    try { hasStored = !!sessionStorage.getItem(STORAGE_KEY); } catch { hasStored = false; }
    if (!hasStored) {
      const fromAi: UtmParams = { utm_source: ai, utm_medium: 'ai-referral' };
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromAi)); } catch { /* request-scoped only */ }
      return fromAi;
    }
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const stored = JSON.parse(raw) as UtmParams;
    const result: UtmParams = {};
    for (const key of KEYS) {
      const value = clean(stored?.[key] ?? null);
      if (value) result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
}

/** AI assistant hosts recognised as referrers (exact host or subdomain). */
const AI_REFERRER_HOSTS: Record<string, string> = {
  'chatgpt.com': 'chatgpt', 'chat.openai.com': 'chatgpt',
  'perplexity.ai': 'perplexity',
  'claude.ai': 'claude',
  'gemini.google.com': 'gemini',
  'copilot.microsoft.com': 'copilot',
  'chat.mistral.ai': 'mistral',
  'chat.deepseek.com': 'deepseek',
};

/** Returns a short AI source label for a referrer URL, or undefined. */
export function aiReferrerSource(referrer: string): string | undefined {
  let host = '';
  try { host = new URL(referrer).hostname.toLowerCase(); } catch { return undefined; }
  for (const [h, label] of Object.entries(AI_REFERRER_HOSTS)) {
    if (host === h || host.endsWith('.' + h)) return label;
  }
  return undefined;
}
